import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScreenBackdrop } from './ScreenBackdrop';
import { PILL_INPUT } from './inputStyles';
import { Numpad, applyAmountKey, type NumpadKey } from './Numpad';
import { BottomSheet } from './BottomSheet';
import { ProgressButton } from './ProgressButton';
import { Confetti } from './Confetti';
import { DiagonalArrow, ChevronRight, WarningIcon } from './icons';
import { IncptIcon } from '../icons/IncptIcon';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';
import { useMe } from '../../hooks/useMe';
import { usePresale } from '../../hooks/usePresale';
import { useIncptTransfers, useResolveIncptRecipient } from '../../hooks/useIncptTransfers';
import { ReauthFlow } from './ReauthFlow';
import { formatUsd } from '../../lib/format';
import {
  decimalGte,
  decimalGt,
  decimalIsPositive,
  decimalToApproxNumber,
  formatDecimalDisplay,
  normalizeDecimalString,
  DEFAULT_INCPT_TRANSFER_LIMITS,
} from '@incpt/shared';
import { parseTelegramId, parseTelegramUsername } from '../../lib/recipients';

function parseIncptSendError(raw: string, t: (k: string) => string): string {
  if (/user_not_found/i.test(raw)) return t('incpt.send.errUserNotFound');
  if (/self_transfer/i.test(raw)) return t('incpt.send.errSelf');
  if (/insufficient_balance/i.test(raw)) return t('incpt.send.insufficient');
  if (/below_min_amount/i.test(raw)) return t('incpt.send.errMinAmount');
  if (/daily_limit_reached/i.test(raw)) return t('incpt.send.errDailyLimit');
  if (/cooldown/i.test(raw)) return t('incpt.send.errCooldown');
  if (/reauth_required/i.test(raw)) return t('incpt.send.errGeneric');
  if (/kill_switch/i.test(raw)) return t('incpt.send.errGeneric');
  if (/user_blocked/i.test(raw)) return t('incpt.send.errBlocked');
  return t('incpt.send.errGeneric');
}

/** Off-chain INCPT P2P send — same layout as SendScreen; TG ID or @username, fee free. */
export function IncptSendScreen({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  useBackButton(onClose);
  const tgBack = hasTelegramBackButton();
  const { me, initData, canFetch } = useMe();
  const { balance, round } = usePresale();
  const { send } = useIncptTransfers();

  const [amount, setAmount] = useState('0');
  const [recipientRaw, setRecipientRaw] = useState('');
  const [resolveTo, setResolveTo] = useState<
    { tgUserId: string } | { username: string } | null
  >(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Same keyboard-dock behaviour as SendScreen.
  const [addressFocused, setAddressFocused] = useState(false);
  const [dockTop, setDockTop] = useState<number | null>(null);
  const [dockFlowHeight, setDockFlowHeight] = useState(0);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const addressDockRef = useRef<HTMLDivElement>(null);
  const addressFocusedRef = useRef(false);
  const restingViewportHeightRef = useRef(0);
  const nativeKeyboardSeenRef = useRef(false);

  const idempotencyRef = useRef<string | null>(null);
  useEffect(() => {
    idempotencyRef.current = null;
  }, [amount, recipientRaw]);

  const clearAddressMode = () => {
    addressFocusedRef.current = false;
    nativeKeyboardSeenRef.current = false;
    setAddressFocused(false);
    setDockTop(null);
    setDockFlowHeight(0);
  };

  /** Sit the address+send dock on the visible viewport, just above the keys. */
  const stickAddressDock = () => {
    const dock = addressDockRef.current;
    const vv = window.visualViewport;
    if (!dock || !vv || !addressFocusedRef.current) return;
    const rect = dock.getBoundingClientRect();
    const delta = vv.height - 8 - rect.bottom;
    if (Math.abs(delta) < 2) return;
    setDockTop((top) => Math.max(8, Math.round((top ?? rect.top) + delta)));
  };

  useEffect(() => {
    const viewportHeight = () => window.visualViewport?.height ?? window.innerHeight;
    restingViewportHeightRef.current = viewportHeight();
    const onResize = () => {
      const h = viewportHeight();
      if (h >= restingViewportHeightRef.current - 24) {
        restingViewportHeightRef.current = Math.max(restingViewportHeightRef.current, h);
        if (addressFocusedRef.current && nativeKeyboardSeenRef.current) {
          addressInputRef.current?.blur();
          clearAddressMode();
        }
      } else if (addressFocusedRef.current) {
        nativeKeyboardSeenRef.current = true;
        stickAddressDock();
      }
    };
    const onScroll = () => {
      if (addressFocusedRef.current && nativeKeyboardSeenRef.current) stickAddressDock();
    };
    window.visualViewport?.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    return () => {
      window.visualViewport?.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    const id = parseTelegramId(recipientRaw);
    if (id) {
      const timer = window.setTimeout(() => setResolveTo({ tgUserId: id }), 350);
      return () => window.clearTimeout(timer);
    }
    const username = parseTelegramUsername(recipientRaw);
    if (username) {
      const timer = window.setTimeout(() => setResolveTo({ username }), 350);
      return () => window.clearTimeout(timer);
    }
    setResolveTo(null);
  }, [recipientRaw]);

  const { recipient, resolving, failed, notFound } = useResolveIncptRecipient(resolveTo);

  const needsReauth = !!me?.pinSet || !!me?.biometricEnabled;
  const available = typeof balance === 'string' ? balance : String(balance ?? '0');
  const minAmount = String(DEFAULT_INCPT_TRANSFER_LIMITS.minAmount);
  const amountNorm = normalizeDecimalString(amount);
  const amountOk =
    amountNorm != null &&
    decimalIsPositive(amountNorm) &&
    decimalGte(amountNorm, minAmount);
  const belowMin =
    amountNorm != null && decimalIsPositive(amountNorm) && !decimalGte(amountNorm, minAmount);
  const exceeds =
    amountNorm != null && decimalIsPositive(amountNorm) && decimalGt(amountNorm, available);
  const resolvedTgId = recipient?.tgUserId ?? null;
  const recipientValid = !!recipient && !!resolvedTgId;
  const idLookupFailed = !!resolveTo && (notFound || failed);
  const badRecipient =
    recipientRaw.trim().length > 0 &&
    !parseTelegramId(recipientRaw) &&
    !parseTelegramUsername(recipientRaw) &&
    !resolving;
  const submitting = sending || send.isPending;
  const canSend =
    recipientValid &&
    amountOk &&
    !exceeds &&
    !submitting &&
    !resolving &&
    !failed;

  const handleKey = useCallback((k: NumpadKey) => {
    setAmount((prev) => applyAmountKey(prev, k, 8));
  }, []);

  const fillMax = () => {
    const norm = normalizeDecimalString(available);
    setAmount(norm && decimalIsPositive(norm) ? norm : '0');
  };

  const submit = async (reauthToken?: string) => {
    if (!resolvedTgId || !amountNorm) return;
    setError(null);
    setSending(true);
    if (!idempotencyRef.current) idempotencyRef.current = globalThis.crypto.randomUUID();
    try {
      await send.mutateAsync({
        toTgUserId: resolvedTgId,
        amount: amountNorm,
        idempotencyKey: idempotencyRef.current,
        reauthToken,
      });
      setSent(true);
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      const definitive =
        /API (400|401|402|403|404|409|429)\b/.test(raw) ||
        /insufficient_balance|below_min_amount|user_not_found|self_transfer|daily_limit_reached|cooldown|idempotency_conflict|user_blocked|reauth_required|kill_switch|idempotency_key/i.test(
          raw,
        );
      if (definitive) idempotencyRef.current = null;
      setError(parseIncptSendError(raw, t));
    } finally {
      setSending(false);
    }
  };

  const onConfirmSend = () => {
    setConfirmOpen(false);
    if (needsReauth) setReauthOpen(true);
    else void submit();
  };

  useEffect(() => {
    if (!sent) return;
    const id = window.setTimeout(onClose, 5000);
    return () => window.clearTimeout(id);
  }, [sent, onClose]);

  useEffect(() => {
    if (!error) return;
    const id = window.setTimeout(() => setError(null), 3200);
    return () => window.clearTimeout(id);
  }, [error]);

  const displayName =
    recipient?.displayName?.trim() ||
    (resolvedTgId ? `ID ${resolvedTgId}` : '');
  const usdApprox = decimalToApproxNumber(amountNorm ?? '0') * round.marketPrice;

  return (
    <div className="relative flex h-full min-h-full flex-col">
      <ScreenBackdrop full />
      <div className="relative z-10 flex h-full min-h-full flex-col px-4 pb-[max(1.25rem,var(--tg-safe-bottom))] pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        {/* Header — same pill as SendScreen, no wallet switcher for off-chain. */}
        <div className="relative flex h-10 items-center justify-center">
          {!tgBack && (
            <button
              onClick={onClose}
              className="absolute left-0 flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.35] text-white"
              aria-label="Back"
            >
              <ChevronRight size={18} className="rotate-180 text-white" />
            </button>
          )}
          <div className="flex items-center gap-1.5 rounded-full bg-white py-1.5 pl-1.5 pr-3 shadow-card">
            <IncptIcon size={24} />
            <span className="text-[14px] font-medium text-ink">{round.symbol}</span>
          </div>
        </div>

        {/* Amount */}
        <div
          className="mt-10 flex flex-col items-center gap-2 text-center"
          onPointerDown={() => {
            if (!addressFocusedRef.current) return;
            addressInputRef.current?.blur();
            clearAddressMode();
          }}
        >
          <div className="flex items-center gap-2">
            <p className="text-[14px] text-muted">
              {t('send.available')}{' '}
              <span className="font-medium text-ink">
                {formatDecimalDisplay(available)} {round.symbol}
              </span>
            </p>
            <button
              type="button"
              onClick={fillMax}
              disabled={!decimalIsPositive(available)}
              className="rounded-full bg-[#eef2ff] px-2.5 py-0.5 text-[12px] font-semibold uppercase tracking-wide text-[#377dff] active:opacity-70 disabled:opacity-40"
            >
              {t('send.max')}
            </button>
          </div>
          <p className="text-[56px] font-medium leading-none tabular-nums">
            <span className={exceeds ? 'text-[#ff3b30]' : 'text-ink'}>
              {amount.slice(0, -1)}
              <span key={amount} className="pin-pop">
                {amount.slice(-1)}
              </span>
            </span>{' '}
            <span className="text-muted">{round.symbol}</span>
          </p>
          {exceeds ? (
            <p className="flex items-center gap-1.5 text-[14px] font-medium text-[#ff3b30]">
              <WarningIcon size={16} />
              {t('incpt.send.insufficient')}
            </p>
          ) : belowMin ? (
            <p className="flex items-center gap-1.5 text-[14px] font-medium text-[#ff3b30]">
              <WarningIcon size={16} />
              {t('incpt.send.errMinAmount')}
            </p>
          ) : (
            <p className="text-[14px] text-muted">≈ ${formatUsd(usdApprox)}</p>
          )}
          {recipientValid && (
            <p className="text-[13px] font-medium text-positive">{t('incpt.send.feeFree')}</p>
          )}
        </div>

        <div className="flex-1" />

        {dockFlowHeight > 0 && (
          <div aria-hidden className="shrink-0" style={{ height: dockFlowHeight }} />
        )}

        <div
          ref={addressDockRef}
          className={
            dockTop != null
              ? 'fixed inset-x-4 z-20 flex flex-col'
              : 'mb-7 flex shrink-0 flex-col'
          }
          style={dockTop != null ? { top: dockTop } : undefined}
        >
          {/* iOS clips the first glyph when radius/pad are on a wrapper — put
              them on the <input> (same as TokenSheet / search fields). */}
          <input
            ref={addressInputRef}
            value={recipientRaw}
            inputMode="text"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => setRecipientRaw(e.target.value)}
            onFocus={() => {
              const dock = addressDockRef.current;
              const top = dock?.getBoundingClientRect().top ?? null;
              addressFocusedRef.current = true;
              nativeKeyboardSeenRef.current = false;
              setDockFlowHeight(dock?.offsetHeight ?? 0);
              setAddressFocused(true);
              setDockTop(top);
            }}
            onBlur={() => {
              clearAddressMode();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            enterKeyHint="done"
            placeholder={t('incpt.send.recipientPlaceholder')}
            className={`w-full ${PILL_INPUT} ${
              badRecipient || idLookupFailed ? 'ring-1 ring-[#ff3b30]' : ''
            }`}
          />
          {resolving && (
            <p className="mt-2 flex items-center gap-1.5 px-1 text-[13px] text-muted">
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-black/15 border-t-[#377dff]" />
              {t('incpt.send.resolving')}
            </p>
          )}
          {recipientValid && (
            <p className="mt-2 px-1 text-[13px] font-medium text-positive">
              {t('incpt.send.idResolved', {
                id: resolvedTgId,
                name: displayName,
              })}
            </p>
          )}
          {idLookupFailed && (
            <p className="mt-2 flex items-center gap-1.5 px-1 text-[13px] font-medium text-[#ff3b30]">
              <WarningIcon size={15} />
              {t('incpt.send.errUserNotFound')}
            </p>
          )}
          {badRecipient && !idLookupFailed && (
            <p className="mt-2 flex items-center gap-1.5 px-1 text-[13px] font-medium text-[#ff3b30]">
              <WarningIcon size={15} />
              {t('incpt.send.errBadRecipient')}
            </p>
          )}

          {sent ? (
            <button
              type="button"
              onClick={onClose}
              className="mt-4 flex h-14 w-full items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90"
            >
              {t('common.done')}
            </button>
          ) : (
            <ProgressButton
              active={submitting}
              done={false}
              onClick={() => setConfirmOpen(true)}
              disabled={!canSend}
              className={`mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-ink text-[15px] font-medium text-white transition-opacity ${
                submitting
                  ? 'pointer-events-none'
                  : canSend
                    ? 'active:opacity-90'
                    : 'pointer-events-none opacity-40'
              }`}
            >
              {submitting ? (
                <>
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  {t('incpt.send.sending')}
                </>
              ) : (
                <>
                  <DiagonalArrow size={20} className="text-white" />
                  {t('incpt.send.submit')}
                </>
              )}
            </ProgressButton>
          )}
        </div>

        {!addressFocused && (
          <div className="mt-4 shrink-0">
            <Numpad onKey={handleKey} />
          </div>
        )}
      </div>

      <BottomSheet
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t('incpt.send.confirmTitle')}
      >
        <div className="mt-5 flex flex-col">
          <Row label={t('incpt.send.amount')} value={`${amount} ${round.symbol}`} />
          <Row label={t('incpt.send.recipient')} value={displayName} />
          <Row label={t('incpt.send.fee')} value={t('incpt.send.feeFree')} />
          <Row
            label={t('send.total')}
            value={`${amount} ${round.symbol}`}
            accent
          />
        </div>
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={() => setConfirmOpen(false)}
            className="h-14 flex-[2] rounded-full bg-[#f2f3f4] text-[15px] font-medium text-ink active:bg-black/[0.06]"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirmSend}
            disabled={submitting}
            className={`flex h-14 flex-[3] items-center justify-center gap-2 rounded-full bg-ink text-[15px] font-medium text-white ${
              submitting ? 'opacity-60' : 'active:opacity-90'
            }`}
          >
            {submitting ? t('incpt.send.sending') : t('incpt.send.submit')}
            {!submitting && <DiagonalArrow size={18} className="text-white" />}
          </button>
        </div>
      </BottomSheet>

      {reauthOpen && canFetch && (
        <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-holo">
          <ReauthFlow
            initData={initData}
            biometricEnabled={!!me?.biometricEnabled}
            reason={t('reauth.reasonSend')}
            onSuccess={(token) => {
              setReauthOpen(false);
              void submit(token);
            }}
            onCancel={() => setReauthOpen(false)}
          />
        </div>
      )}

      {error && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1.25rem)]">
          <div className="animate-toast flex items-center gap-3 rounded-[20px] bg-ink px-5 py-4 text-white shadow-[0px_16px_40px_rgba(0,0,0,0.28)]">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ff3b30]">
              <WarningIcon size={16} className="text-white" />
            </span>
            <span className="text-[14px] font-medium leading-tight">{error}</span>
          </div>
        </div>
      )}

      {sent && <Confetti />}
      <SendStatusPill
        visible={submitting || sent}
        sent={sent}
        amount={amount}
        symbol={round.symbol}
        sendingLabel={t('incpt.send.sending')}
        sentLabel={t('incpt.send.sentTitle')}
      />
    </div>
  );
}

function SendStatusPill({
  visible,
  sent,
  amount,
  symbol,
  sendingLabel,
  sentLabel,
}: {
  visible: boolean;
  sent: boolean;
  amount: string;
  symbol: string;
  sendingLabel: string;
  sentLabel: string;
}) {
  if (!visible) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1.25rem)]">
      <div className="animate-toast flex items-center gap-3 rounded-[20px] bg-ink px-5 py-4 text-white shadow-[0px_16px_40px_rgba(0,0,0,0.28)]">
        {!sent && (
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        )}
        <span className="text-[14px] font-medium leading-tight">
          {sent ? sentLabel : `${sendingLabel} ${amount} ${symbol}`}
        </span>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-black/[0.06] py-3.5 text-[14px] last:border-b-0">
      <span className="text-muted">{label}</span>
      <span className={`truncate font-medium ${accent ? 'text-ink' : 'text-ink'}`}>{value}</span>
    </div>
  );
}
